import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from './api';

export interface Session {
  username: string;
}

export const SESSION_KEY = ['session'] as const;

/**
 * Who is signed in, or `null`. A 401 is an answer, not an error — it is how
 * the app learns to render the login screen — so it resolves rather than
 * throws and is never retried.
 */
export function useSession() {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: async (): Promise<Session | null> => {
      try {
        return await api.get<Session>('/auth/me');
      } catch (error) {
        if (error instanceof ApiError && error.isUnauthorized) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentials: { username: string; password: string }) =>
      api.post<Session>('/auth/login', credentials),
    onSuccess: (session) => {
      queryClient.setQueryData(SESSION_KEY, session);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSuccess: () => {
      // Drop every cached admin response, not just the session: the next
      // operator to sign in must not see the previous one's data.
      queryClient.clear();
      queryClient.setQueryData(SESSION_KEY, null);
    },
  });
}
